import type {
	Config,
	Balance,
	Position,
	Portfolio,
	LatestData,
	HistoricalData,
	SignalsData,
	LogsData,
	PositionHistoryResponse,
	OrdersResponse
} from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

async function fetchJson<T>(url: string): Promise<T> {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}
	return response.json();
}

export const api = {
	getConfig: () => fetchJson<Config>(`${API_BASE}/api/config`),
	getBalance: () => fetchJson<Balance>(`${API_BASE}/api/balance`),
	getPositions: () => fetchJson<{ positions: Position[] }>(`${API_BASE}/api/positions`),
	getPortfolio: () => fetchJson<Portfolio>(`${API_BASE}/api/portfolio`),
	getLatest: (symbol: string) => fetchJson<LatestData>(`${API_BASE}/api/latest/${symbol}`),
	getHistory: (symbol: string, limit = 100) =>
		fetchJson<HistoricalData>(`${API_BASE}/api/history/${symbol}?limit=${limit}`),
	getSignals: (symbol: string, limit = 50, cursor?: string, types?: string[]) => {
		const params = new URLSearchParams();
		params.append('limit', String(limit));
		if (cursor) {
			params.append('cursor', cursor);
		}
		if (types && types.length > 0) {
			params.append('types', types.join(','));
		}
		return fetchJson<SignalsData>(`${API_BASE}/api/signals/${symbol}?${params.toString()}`);
	},
	getLatestSignal: (symbol: string) =>
		fetchJson<{ symbol: string; signal: SignalsData['signals'][0] }>(
			`${API_BASE}/api/signals/${symbol}/latest`
		),
	getLogs: (limit = 50, cursor?: string, levels?: string[]) => {
		const params = new URLSearchParams();
		params.append('limit', String(limit));
		if (cursor) {
			params.append('cursor', cursor);
		}
		if (levels && levels.length > 0) {
			levels.forEach((level) => params.append('level', level));
		}
		return fetchJson<LogsData>(`${API_BASE}/api/logs?${params.toString()}`);
	},
	getPositionHistory: (page = 1, limit = 10, symbol?: string) => {
		const params = new URLSearchParams();
		params.append('page', String(page));
		params.append('limit', String(limit));
		if (symbol) {
			params.append('symbol', symbol);
		}
		return fetchJson<PositionHistoryResponse>(
			`${API_BASE}/api/position-history?${params.toString()}`
		);
	},
	getOrders: (page = 1, limit = 25, symbol?: string) => {
		const params = new URLSearchParams();
		params.append('page', String(page));
		params.append('limit', String(limit));
		if (symbol) {
			params.append('symbol', symbol);
		}
		return fetchJson<OrdersResponse>(`${API_BASE}/api/orders?${params.toString()}`);
	}
};
